import React from 'react';
import { motion } from 'framer-motion';
import { Button } from './ui/Button';
import { ShieldCheck, Lock, CreditCard, Users, Star } from 'lucide-react';
export const Hero = () => {
  const scrollToPricing = () => {
    document.getElementById('pricing')?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  return <section className="relative min-h-screen bg-navy flex items-center pt-28 pb-16 overflow-hidden" data-id="element-118">
      <div className="absolute inset-0 bg-noise opacity-20 mix-blend-overlay pointer-events-none" data-id="element-119"></div>

      {/* Decorative background elements */}
      <div className="absolute top-1/4 left-0 w-[500px] h-[500px] bg-gold-500/5 rounded-full blur-3xl -translate-x-1/2" data-id="element-120"></div>
      <div className="absolute bottom-0 right-0 w-[600px] h-[600px] bg-gold-500/5 rounded-full blur-3xl translate-x-1/3 translate-y-1/3" data-id="element-121"></div>

      <div className="container mx-auto px-6 relative z-10" data-id="element-122">
        <div className="max-w-4xl mx-auto text-center" data-id="element-123">
          {/* Subtitle (above h1) */}
          <motion.p initial={{
          opacity: 0,
          y: -10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} className="text-gold-400 text-sm md:text-base font-serif italic tracking-widest uppercase mb-6" data-id="element-124">
            5 Módulos Clínicos + 3 Bonos Premium
          </motion.p>

          {/* H1 — Main Title */}
          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.7,
          delay: 0.1
        }} className="text-5xl md:text-7xl lg:text-8xl font-bold text-cream font-serif leading-[1.05] mb-6 text-balance" data-id="element-125">
            Protocolo Clínico{' '}
            <span className="text-gold-500 italic" data-id="element-126">Holístico</span>
          </motion.h1>

          {/* Ornamental divider */}
          <motion.div initial={{
          opacity: 0,
          scaleX: 0
        }} animate={{
          opacity: 1,
          scaleX: 1
        }} transition={{
          duration: 0.6,
          delay: 0.3
        }} className="flex items-center justify-center gap-4 mb-6" data-id="element-127">
            <span className="h-px w-16 bg-gold-500/40" data-id="element-128"></span>
            <span className="text-gold-500 text-xl" data-id="element-129">✦</span>
            <span className="h-px w-16 bg-gold-500/40" data-id="element-130"></span>
          </motion.div>

          {/* Promise (subhead under h1) */}
          <motion.p initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.4
        }} className="text-lg md:text-2xl text-cream-200 font-serif italic leading-relaxed mb-10 max-w-3xl mx-auto text-balance" data-id="element-131">
            Todo tu protocolo clínico holístico resuelto en 30 días — integrando
            Psicología, Constelaciones Familiares, Biodecodificación y Registros
            Akáshicos con absoluto rigor clínico.
          </motion.p>

          {/* CTA */}
          <motion.div initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.5
        }} className="mb-8" data-id="element-132">
            <Button size="lg" onClick={scrollToPricing} className="text-base md:text-lg" data-id="element-133">
              💥 Quiero mi práctica clínica resuelta ahora
            </Button>
          </motion.div>

          {/* Trust badges */}
          <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          duration: 0.6,
          delay: 0.7
        }} className="grid grid-cols-2 md:grid-cols-5 gap-4 text-cream-200/80 text-xs font-medium max-w-3xl mx-auto mb-12" data-id="element-134">
            <div className="flex flex-col items-center text-center gap-2" data-id="element-135">
              <ShieldCheck className="w-5 h-5 text-gold-500" data-id="element-136" />
              <span data-id="element-137">7 días garantía</span>
            </div>
            <div className="flex flex-col items-center text-center gap-2" data-id="element-138">
              <Lock className="w-5 h-5 text-gold-500" data-id="element-139" />
              <span data-id="element-140">Pago seguro</span>
            </div>
            <div className="flex flex-col items-center text-center gap-2" data-id="element-141">
              <CreditCard className="w-5 h-5 text-gold-500" data-id="element-142" />
              <span data-id="element-143">Mercado Pago</span>
            </div>
            <div className="flex flex-col items-center text-center gap-2" data-id="element-144">
              <Users className="w-5 h-5 text-gold-500" data-id="element-145" />
              <span data-id="element-146">+500 Terapeutas</span>
            </div>
            <div className="flex flex-col items-center text-center gap-2 col-span-2 md:col-span-1" data-id="element-147">
              <div className="flex text-gold-500" data-id="element-148">
                {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 fill-current" data-id="element-149" />)}
              </div>
              <span data-id="element-150">4.9 Rating</span>
            </div>
          </motion.div>

          {/* Book Mockup — centered below, at 70% scale */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.9
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 1,
          delay: 0.6
        }} className="relative flex items-center justify-center" data-id="element-151">
            <motion.div animate={{
            y: [-10, 10, -10]
          }} transition={{
            repeat: Infinity,
            duration: 6,
            ease: 'easeInOut'
          }} className="relative z-10 w-[70%] max-w-[500px] mx-auto" data-id="element-152">
              <div className="absolute inset-0 bg-gold-500/20 blur-[100px] rounded-full" data-id="element-153"></div>
              <img src="https://cdn.magicpatterns.com/uploads/o3mffLzW7yXi4n7kpzoUrL/WhatsApp_Image_2026-06-02_at_19.30.15-Photoroom.png" alt="La Memoria del Alma — Libro y cartas del protocolo clínico" width={1240} height={1240} loading="eager" decoding="async" fetchPriority="high" className="relative z-10 w-full h-auto drop-shadow-2xl" data-id="element-154" />
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>;
};